import { Component, Input, Signal, TemplateRef, computed, inject, OnInit } from '@angular/core';
import { FailedTranscodingTypes } from '../pdf-transcode-error/pdf-transcode-error.component';
import { MediaPlayerFile, MediaTranscodingStatus } from '../../../../interfaces/media-player-store.interface';
import { MediaPlayerStoreService, MediaPlayerStore } from '../../../../services/media-player-store.service';
import { FileTranscodingStatus } from '../../../../models/media-player-types';

@Component({
  selector: 'pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrl: './pdf-viewer.component.scss',
  standalone: false,
})
export class PdfViewerComponent implements OnInit {
  @Input() instanceId: string;
  @Input() assetListTemplate!: TemplateRef<unknown>;
  @Input() assetInfoTemplate!: TemplateRef<unknown>;
  @Input() enablePage: boolean = false;

  private readonly mpStoreService: MediaPlayerStoreService = inject(MediaPlayerStoreService);
  mpStore: MediaPlayerStore | null = null;

  readonly TranscodingTypes: typeof FailedTranscodingTypes = FailedTranscodingTypes;
  readonly FileTranscodingStatus = FileTranscodingStatus;

  // Direct signals from mpStore
  readonly mediaTypeSignal: Signal<string | null> = computed(() => this.mpStore?.mediaTypeSignal() ?? null);
  readonly itemSignal: Signal<MediaPlayerFile | null> = computed(() => this.mpStore?.itemSignal() ?? null);
  readonly annotationsSignal: Signal<unknown[]> = computed(() => this.mpStore?.annotationsSignal() ?? []);
  readonly projectIdSignal: Signal<string | null> = computed(() => this.mpStore?.projectIdSignal() ?? null);
  readonly sectionIdSignal: Signal<string | null> = computed(() => this.mpStore?.sectionIdSignal() ?? null);
  readonly subsectionIdSignal: Signal<string | null> = computed(() => this.mpStore?.subsectionIdSignal() ?? null);
  readonly typeIdSignal: Signal<string | null> = computed(() => this.mpStore?.typeIdSignal() ?? null);
  readonly subsectionStateSignal: Signal<any> = computed(() => this.mpStore?.subsectionStateSignal() ?? null);
  readonly hideAnnotationToolbarSignal: Signal<boolean> = computed(
    () => this.mpStore?.hideAnnotationToolbarSignal() ?? false
  );
  readonly enableAnnotationSignal: Signal<boolean> = computed(() => this.mpStore?.enableAnnotationSignal() ?? false);
  readonly commentOnlySignal: Signal<boolean> = computed(() => this.mpStore?.commentOnlySignal() ?? false);
  readonly activePageSignal: Signal<any> = computed(() => this.mpStore?.activePageSignal() ?? null);
  readonly useFallbackDisplaySignal: Signal<boolean> = computed(
    () => this.mpStore?.useFallbackDisplaySignal() ?? false
  );
  readonly hasEditPermissionSignal: Signal<boolean> = computed(() => this.mpStore?.hasEditPermissionSignal() ?? false);
  readonly userInfoSignal: Signal<Record<string, unknown> | null> = computed(
    () => this.mpStore?.userInfoSignal() ?? null
  );
  readonly fileTranscodingStatusSignal: Signal<MediaTranscodingStatus> = computed(
    () => this.mpStore?.fileTranscodingStatusSignal() ?? FileTranscodingStatus.IDLE
  );
  readonly isLoadingSignal: Signal<boolean> = computed(() => this.mpStore?.isLoadingSignal() ?? false);
  readonly isReleasePreviewSignal: Signal<boolean> = computed(() => this.mpStore?.isReleasePreviewSignal() ?? false);
  readonly annotationsLoadedSignal: Signal<boolean> = computed(() => this.mpStore?.annotationsLoadedSignal() ?? false);
  readonly projectParticipantsSignal: Signal<unknown[]> = computed(
    () => this.mpStore?.projectParticipantsSignal() ?? []
  );
  readonly isLoadingOrBeingTranscodedSignal: Signal<boolean> = computed(
    () => this.mpStore?.isLoadingOrBeingTranscoded() ?? false
  );

  ngOnInit(): void {
    this.mpStore = this.mpStoreService.getStore(this.instanceId);
    if (!this.mpStore) {
      throw new Error(`Failed to get media player store for instanceId: ${this.instanceId}`);
    }
  }
}
